import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import DynamicHeader from "../DynamicComponents/DynamicHeader";
import { useThemeContext } from "../../context/ThemeContext";
import {
  FaUser,
  FaCalendarAlt,
  FaHandshake,
  FaBriefcase,
  FaPoll,
  FaRegThumbsUp,
  FaThumbsUp,
  FaPaperclip,
  FaTimes,
  FaInfoCircle,
} from "react-icons/fa";
import { FaTrash } from "react-icons/fa";
import { useSelector } from "react-redux";
import API from "../../utils/API";
import Loader from "../Loader";
import Modal from "./Modal";

const EchoesDTU = () => {
  const user = useSelector((state) => state.auth.user) || {};
  const { roll_no, faculty_id, department_id, batch, user_id } = user;
  const { darkMode } = useThemeContext();
  const [activeTab, setActiveTab] = useState("feed");
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newPost, setNewPost] = useState({
    title: "",
    body: "",
    poll_options: [],
    document: null,
  });
  const [pollOptionInputs, setPollOptionInputs] = useState(["", ""]);
  const [currentPage, setCurrentPage] = useState(1);
  const [documentFile, setDocumentFile] = useState(null);
  const { role } = useSelector((state) => state.user) || {};
  // Helper function to get initials from name
  const getInitials = (name) => {
    if (!name) return "U"; // Default if no name

    const names = name.split(" ");
    let initials = names[0].charAt(0).toUpperCase();

    if (names.length > 1) {
      initials += names[names.length - 1].charAt(0).toUpperCase();
    }

    return initials;
  };
  // Category descriptions
  const categoryDescriptions = {
    general:
      "Stay informed with official announcements, exciting updates, and engaging campus-wide conversations where students share news, tips, and daily life stories.",
    polls:
      "Create insightful polls and cast your vote on hot topics—shape decisions, spark debates, and see what your peers really think.",
    events:
      "Explore upcoming seminars, socials, workshops, and gatherings—mark your calendar and join events that bring our community together.",
    "Lost & Found":
      "Report items you’ve misplaced or claim goods that others have found—let’s work together to reunite belongings with their owners.",
    "Team Up":
      "Browse profiles, pitch your skills, and recruit passionate partners for projects, hackathons, or study groups—collaboration starts here.",
    "My Posts":
      "Review and manage every discussion, announcement, and question you’ve authored—keep track of your contributions in one place.",
    "Jobs & Opportunities":
      "Discover curated internships, part‑time roles, and career‑building opportunities designed to help you grow professionally and land your next challenge.",
    feed: "Dive into a dynamic, real-time stream of updates, discussions, and discoveries—your personalized window into everything happening across campus.",
  };
  // Available tags mapped to tabs
  const tabToTagMapping = {
    // Default for feed view
    general: "General",
    polls: "Polls",
    events: "Events",
    "Lost & Found": "Lost & Found",
    "Team Up": "Team Up",
    "Jobs & Opportunities": "Jobs & Opportunities",
  };

  // Fetch posts from API
  const fetchPosts = async (pageNum = 1, reset) => {
    if ((!hasMore && pageNum !== 1) || isLoading) return;

    setIsLoading(true);
    try {
      const params = {
        page: pageNum,
      };
      if (activeTab === "My Posts") {
        params.my_posts = true;
      } else if (activeTab !== "feed") {
        params.tag = tabToTagMapping[activeTab];
      }

      const response = await API.get(
        `ece/${role}/posts?user_id=${roll_no || faculty_id || user_id}`,
        { params }
      );
      const newPosts = response.data.posts || [];

      if (reset) {
        setPosts(newPosts);
        console.log("Posts reset:", newPosts);
      } else {
        setPosts((prev) => [...prev, ...newPosts]);
      }

      setHasMore(newPosts.length >= 15);
      setCurrentPage(pageNum);
      if (newPosts.length > 0) {
        setPage(pageNum + 1);
      }
    } catch (error) {
      toast.error("Failed to fetch posts");
      console.error("Error fetching posts:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      setIsLoading(true);
      await API.delete(`ece/${role}/posts/${postId}`, {
        params: { user_id: roll_no || faculty_id || user_id },
      });

      toast.success("Post deleted successfully");

      // Remove the deleted post from state
      setPosts(posts.filter((post) => post.post_id !== postId));
      if (selectedPost?.post_id === postId) {
        setSelectedPost(null);
      }
    } catch (error) {
      toast.error("Failed to delete post");
      console.error("Error deleting post:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load and when tab changes
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchPosts(1, true);

    // Set the tag based on active tab when it changes
    if (activeTab !== "feed") {
      setNewPost((prev) => ({
        ...prev,
        tags: [tabToTagMapping[activeTab]],
      }));
    }
  }, [activeTab]);

  // Handle infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop !==
          document.documentElement.offsetHeight ||
        isLoading ||
        !hasMore
      ) {
        return;
      }
      fetchPosts(page, false);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLoading, hasMore, page]);

  // Handle document upload
  const handleDocumentUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        toast.error("File size should be less than 5MB");
        return;
      }
      setDocumentFile(file);
      setNewPost({ ...newPost, document: file.name });
    }
  };

  // Remove document
  const removeDocument = () => {
    setDocumentFile(null);
    setNewPost({ ...newPost, document: null });
  };

  // Create a new post
  const handleCreatePost = async (e) => {
    e.preventDefault();

    if (!newPost.title || !newPost.body) {
      toast.error("Title and content are required");
      return;
    }

    // For polls, validate options
    if (activeTab === "polls" && newPost.poll_options.length < 2) {
      toast.error("Polls must have at least 2 options");
      return;
    }

    try {
      setIsLoading(true);

      const formData = new FormData();
      formData.append("title", newPost.title);
      formData.append("body", newPost.body);
      // Always use the tag from the current active tab
      formData.append("tags", JSON.stringify([tabToTagMapping[activeTab]]));
      if (newPost.date) {
        formData.append("date", newPost.date);
      }
      if (activeTab === "polls") {
        formData.append(
          "poll_options",
          JSON.stringify(
            newPost.poll_options.filter((opt) => opt.trim() !== "")
          )
        );
      }

      if (documentFile) {
        formData.append("document", documentFile);
      }
      const params = {
        user_id: roll_no || faculty_id,
      };
      const response = await API.post(
        `ece/student/posts?user_id=${roll_no || faculty_id || user_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("Post created successfully");

      // Reset form
      setNewPost({
        title: "",
        body: "",
        tags: [tabToTagMapping[activeTab]],
        poll_options: [],
        document: null,
      });
      setPollOptionInputs(["", ""]);
      setDocumentFile(null);

      // Refresh posts
      fetchPosts(1, true);
    } catch (error) {
      toast.error("Failed to create post");
      console.error("Error creating post:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle upvote
  const handleUpvote = async (postId) => {
    try {
      const params = {
        user_id: roll_no || faculty_id,
        post_id: postId,
      };
      await API.post(`ece/student/posts/upvote/${postId}/`, params);

      // Update local state
      setPosts(
        posts.map((post) => {
          if (post.post_id === postId) {
            const hasUpvoted = !post.has_upvoted;
            return {
              ...post,
              has_upvoted: hasUpvoted,
              upvotes: hasUpvoted ? post.upvotes + 1 : post.upvotes - 1,
            };
          }
          return post;
        })
      );

      // Update selected post if it's the one being upvoted
      if (selectedPost && selectedPost.post_id === postId) {
        setSelectedPost((prev) => ({
          ...prev,
          has_upvoted: !prev.has_upvoted,
          upvotes: prev.has_upvoted ? prev.upvotes - 1 : prev.upvotes + 1,
        }));
      }
    } catch (error) {
      toast.error("Failed to upvote");
      console.error("Error upvoting:", error);
    }
  };

  // Handle poll vote
  const handlePollVote = async (postId, optionId) => {
    const params = {
      user_id: roll_no || faculty_id || user_id,
      post_id: postId,
      option_id: optionId,
    };
    try {
      const response = await API.post(
        `ece/student/posts/vote/${postId}`,
        params
      );

      // Update local state with new poll data
      setPosts(
        posts.map((post) => {
          if (post.post_id === postId) {
            return {
              ...post,
              poll_options: response.data.poll_options,
              user_selected_option_id: optionId,
            };
          }
          return post;
        })
      );

      // Update selected post if it's the one being voted on
      if (selectedPost && selectedPost.post_id === postId) {
        setSelectedPost({
          ...selectedPost,
          poll_options: response.data.poll_options,
          user_selected_option_id: optionId,
        });
      }
    } catch (error) {
      toast.error("Failed to submit vote");
      console.error("Error voting:", error);
    }
  };

  // Add poll option input
  const addPollOption = () => {
    setPollOptionInputs([...pollOptionInputs, ""]);
  };

  // Update poll option
  const updatePollOption = (index, value) => {
    const newOptions = [...pollOptionInputs];
    newOptions[index] = value;
    setPollOptionInputs(newOptions);

    // Update in newPost state if it's a poll
    if (newPost.tags.includes("Polls")) {
      setNewPost({
        ...newPost,
        poll_options: newOptions.filter((opt) => opt.trim() !== ""),
      });
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get user role display text
  const getUserRoleDisplay = (post) => {
    if (post.user_type === "alumni") {
      return `Alumni - Class of ${post.batch}`;
    } else if (post.user_type === "student") {
      return `Student - ${post.department_id} Class of ${post.batch}`;
    } else if (post.user_type === "faculty") {
      return `Faculty - ${post.department_id}`;
    } else if (post.user_type === "department") {
      return `${post.department_id} Department`;
    }
    return post.user_type;
  };

  // Render post form based on active tab
  const renderPostForm = () => {
    return (
      <div className="space-y-3">
        <input
          type="text"
          value={newPost.title}
          onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
          placeholder={
            activeTab === "polls"
              ? "Poll Question"
              : activeTab === "events"
                ? "Event Title"
                : activeTab === "Lost & Found"
                  ? "Item Name (Found/Lost)"
                  : activeTab === "Team Up"
                    ? "Project/Hackathon Name"
                    : activeTab === "Jobs & Opportunities"
                      ? "Opportunity Title"
                      : "Title"
          }
          className="w-full p-2 rounded-lg text-sm"
          style={{
            backgroundColor: darkMode ? "#0D1117" : "#FFFFFF",
            color: darkMode ? "#F8F9FA" : "#1F252E",
            border: darkMode ? "1px solid #2B2C3A" : "1px solid #D1D5DB",
          }}
          required
        />

        <textarea
          value={newPost.body}
          onChange={(e) => setNewPost({ ...newPost, body: e.target.value })}
          placeholder={
            activeTab === "polls"
              ? "Poll Description"
              : activeTab === "events"
                ? "Event Details"
                : activeTab === "Lost & Found"
                  ? "Description and location details"
                  : activeTab === "Team Up"
                    ? "Describe what you're looking for in team members"
                    : activeTab === "Jobs & Opportunities"
                      ? "Describe the opportunity and requirements"
                      : "What's on your mind?"
          }
          className="w-full p-2 rounded-lg text-sm resize-none"
          style={{
            backgroundColor: darkMode ? "#0D1117" : "#FFFFFF",
            color: darkMode ? "#F8F9FA" : "#1F252E",
            border: darkMode ? "1px solid #2B2C3A" : "1px solid #D1D5DB",
            minHeight: "100px",
          }}
          required={activeTab !== "polls"}
        />

        {activeTab === "events" && (
          <div className="flex flex-col">
            <label
              className="text-sm mb-1"
              style={{ color: darkMode ? "#B0B3B8" : "#6B7280" }}
            >
              Event Date
            </label>
            <input
              type="date"
              onChange={(e) => setNewPost({ ...newPost, date: e.target.value })}
              className="w-full p-2 rounded-lg text-sm"
              style={{
                backgroundColor: darkMode ? "#0D1117" : "#FFFFFF",
                color: darkMode ? "#F8F9FA" : "#1F252E",
                border: darkMode ? "1px solid #2B2C3A" : "1px solid #D1D5DB",
              }}
              required
            />
          </div>
        )}

        {activeTab === "polls" && (
          <div className="space-y-2">
            {pollOptionInputs.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={option}
                  onChange={(e) => updatePollOption(index, e.target.value)}
                  placeholder={`Option ${index + 1}`}
                  className="flex-1 p-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: darkMode ? "#0D1117" : "#FFFFFF",
                    color: darkMode ? "#F8F9FA" : "#1F252E",
                    border: darkMode
                      ? "1px solid #2B2C3A"
                      : "1px solid #D1D5DB",
                  }}
                  required={index < 2}
                />
                {index >= 2 && index < 5 && (
                  <button
                    type="button"
                    onClick={() => {
                      const newOptions = [...pollOptionInputs];
                      newOptions.splice(index, 1);
                      setPollOptionInputs(newOptions);
                      setNewPost({
                        ...newPost,
                        poll_options: newOptions.filter(
                          (opt) => opt.trim() !== ""
                        ),
                      });
                    }}
                    className="text-red-500"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {pollOptionInputs.length < 6 && (
              <button
                type="button"
                onClick={addPollOption}
                className="text-xs px-3 py-1 rounded-lg"
                style={{
                  backgroundColor: darkMode ? "#2B2C3A" : "#E5E7EB",
                  color: darkMode ? "#B0B3B8" : "#6B7280",
                }}
              >
                Add Option (Max 6)
              </button>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <label className="flex items-center space-x-2 cursor-pointer">
            <FaPaperclip />
            <span className="text-sm">Attach Document (PDF, JPEG, PNG)</span>
            <input
              type="file"
              onChange={handleDocumentUpload}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
            />
          </label>
          {newPost.document && (
            <div className="flex items-center space-x-2">
              <span className="text-sm truncate max-w-xs">
                {typeof newPost.document === "string"
                  ? newPost.document
                  : newPost.document.name}
              </span>
              <button
                type="button"
                onClick={removeDocument}
                className="text-red-500 hover:text-red-700"
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render poll options
  const renderPollOptions = (post) => {
    if (!post.poll_options || post.poll_options.length === 0) return null;

    const totalVotes = post.poll_options.reduce(
      (sum, opt) => sum + (opt.score || 0),
      0
    );

    return (
      <div className="mt-3 space-y-3">
        {post.poll_options.map((option) => {
          const isSelected =
            option.is_selected ||
            post.user_selected_option_id === option.option_id;
          const percentage =
            totalVotes > 0 ? Math.round((option.score / totalVotes) * 100) : 0;

          return (
            <div key={option.option_id} className="relative">
              <button
                onClick={() =>
                  !isSelected && handlePollVote(post.post_id, option.option_id)
                }
                className={`w-full text-left p-3 rounded-lg transition-all ${isSelected ? "ring-2 ring-[#A41E22]" : ""}`}
                style={{
                  backgroundColor: darkMode ? "#161B22" : "#F3F4F6",
                  color: darkMode ? "#F8F9FA" : "#1F252E",
                }}
                disabled={isSelected}
              >
                <div className="flex justify-between items-center">
                  <span>{option.option_text}</span>
                  <span className="text-sm font-medium">{percentage}%</span>
                </div>
                <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#A41E22]"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </button>
            </div>
          );
        })}
        <div
          className="text-xs text-center pt-1"
          style={{ color: darkMode ? "#B0B3B8" : "#6B7280" }}
        >
          {totalVotes} total votes
        </div>
      </div>
    );
  };

  return (
    <div
      className="pb-4"
      style={{ backgroundColor: darkMode ? "#1E1E2E" : "#F8F8F8" }} // Darker black for dark mode
    >
      <DynamicHeader />
      <div
        className="flex items-start p-3 rounded-lg text-xs mx-auto mb-4 space-x-3"
        style={{
          backgroundColor: darkMode ? "#252525" : "#F5F5F5",
          border: darkMode ? "1px solid #383838" : "1px solid #E0E0E0",
        }}
      >
        <div
          className="flex-shrink-0 p-2 rounded-full mt-0.5"
          style={{
            backgroundColor: "#FDECEA",
          }}
        >
          <FaInfoCircle
            className="text-sm"
            style={{ color: "#A41E22", minWidth: "14px" }}
          />
        </div>
        <div
          className="text-justify leading-relaxed"
          style={{
            color: darkMode ? "#D1D5DB" : "#4B5563",
            fontSize: "0.8125rem", // 13px
          }}
        >
          <span
            className="font-semibold"
            style={{ color: darkMode ? "#F3F4F6" : "#111827" }}
          >
            Community Tip:{" "}
          </span>
          This space brings together DTU students, faculty, and alumni — a great
          mix for sharing ideas, asking questions, and building meaningful
          connections. Let's keep things respectful and thoughtful so everyone
          feels welcome to contribute and collaborate.
        </div>
      </div>
      <div className="flex mt-4 min-h-screen">
        <div
          className="flex-1 overflow-y-auto px-4 mb-10 pb-10"
          style={{ backgroundColor: darkMode ? "#1E1E2E" : "#F8F8F8" }}
        >
          <div
            className="rounded-2xl shadow-xl w-full mb-6"
            style={{
              backgroundColor: darkMode ? "#0D1117" : "#FFFFFF",
              border: darkMode ? "1px solid #252525" : "1px solid #E5E5E5",
              boxShadow: darkMode
                ? "0 4px 12px rgba(0, 0, 0, 0.5)"
                : "0 4px 12px rgba(0, 0, 0, 0.08)",
            }}
          >
            <div className="p-6">
              <h1
                className="text-2xl font-bold mb-2"
                style={{ color: darkMode ? "#FFFFFF" : "#A41E22" }} // Red in light mode
              >
                EchoesDTU Community
              </h1>
              {
                <p
                  className="text-sm mb-2"
                  style={{ color: darkMode ? "#B0B3B8" : "#666666" }}
                >
                  {categoryDescriptions[activeTab]}
                </p>
              }
            </div>

            <div
              className="border-b"
              style={{ borderColor: darkMode ? "#252525" : "#E5E5E5" }}
            >
              <nav className="flex overflow-x-auto px-6">
                {[
                  "feed",
                  "general",
                  "polls",
                  "events",
                  "Lost & Found",
                  "Team Up",
                  "Jobs & Opportunities",
                  "My Posts",
                ].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`whitespace-nowrap py-4 px-3 text-sm font-medium border-b-2 transition-colors focus:outline-none ${
                      activeTab === tab
                        ? darkMode
                          ? "border-[#A41E22] text-[#FFFFFF]"
                          : "border-[#A41E22] text-[#A41E22]"
                        : darkMode
                          ? "border-transparent text-[#B0B3B8] hover:text-[#FFFFFF]"
                          : "border-transparent text-[#666666] hover:text-[#A41E22]"
                    }`}
                  >
                    {tab === "polls" && <FaPoll className="inline mr-1" />}
                    {tab === "events" && (
                      <FaCalendarAlt className="inline mr-1" />
                    )}
                    {tab === "Lost & Found" && (
                      <FaBriefcase className="inline mr-1" />
                    )}
                    {tab === "Team Up" && (
                      <FaHandshake className="inline mr-1" />
                    )}
                    {tab === "Jobs & Opportunities" && (
                      <FaBriefcase className="inline mr-1" />
                    )}
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {/* Create Post Card */}
              {activeTab !== "feed" && activeTab !== "My Posts" && (
                <div
                  className="rounded-xl p-4 mb-6"
                  style={{
                    backgroundColor: darkMode ? "#0D117" : "#FFFFFF",
                    border: darkMode
                      ? "1px solid #252525"
                      : "1px solid #E5E5E5",
                  }}
                >
                  <div className="flex items-start space-x-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: darkMode ? "#252525" : "#A41E22", // Red background in light mode
                      }}
                    >
                      <span
                        className="text-sm font-medium"
                        style={{ color: darkMode ? "#FFFFFF" : "#FFFFFF" }} // White text on both
                      >
                        {getInitials(user.name || user.user_name)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <form onSubmit={handleCreatePost}>
                        {renderPostForm()}
                        <div className="flex justify-end mt-3">
                          <button
                            type="submit"
                            className="px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#8C1A1A] transition-colors"
                            style={{
                              backgroundColor: "#A41E22",
                              color: "#FFFFFF",
                            }}
                            disabled={isLoading}
                          >
                            {isLoading ? "Posting..." : "Post"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}

              {/* Posts Grid */}
              {isLoading && posts.length === 0 ? (
                <div className="flex justify-center items-center py-10">
                  <Loader />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {posts.map((post) => (
                    <div
                      key={post.post_id}
                      className="rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02] flex flex-col h-full hover:shadow-md"
                      style={{
                        backgroundColor: darkMode ? "#161722" : "#FFFFFF",
                        border: darkMode
                          ? "1px solid #252525"
                          : "1px solid #E5E5E5",
                        boxShadow: darkMode
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 4px rgba(0, 0, 0, 0.05)",
                      }}
                      onClick={() => setSelectedPost(post)}
                    >
                      {/* User info */}
                      <div className="flex items-start space-x-3 mb-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{
                            backgroundColor: darkMode ? "#252525" : "#A41E22", // Red background in light mode
                          }}
                        >
                          <span
                            className="text-sm font-medium"
                            style={{ color: "#FFFFFF" }} // Always white text
                          >
                            {getInitials(post.user_name)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3
                              className="font-medium"
                              style={{
                                color: darkMode ? "#FFFFFF" : "#A41E22", // Red in light mode
                              }}
                            >
                              {post.user_name}
                            </h3>
                            <span
                              className="text-xs pl-3 pr-2 py-1 rounded-full"
                              style={{
                                backgroundColor: darkMode
                                  ? "#252525"
                                  : "#F0F0F0",
                                color: darkMode ? "#B0B3B8" : "#666666",
                              }}
                            >
                              {getUserRoleDisplay(post)}
                            </span>
                          </div>
                          {post.email_id && (
                            <a
                              href={`mailto:${post.email_id}`}
                              className="text-xs block mt-1 hover:underline"
                              style={{
                                color: darkMode ? "#A41E22" : "#A41E22",
                              }}
                            >
                              {post.email_id}
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Post content */}
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-1 my-2">
                          {post.tags?.map((tag) => (
                            <span
                              key={tag}
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{
                                backgroundColor: darkMode
                                  ? "#252525"
                                  : "#F0F0F0",
                                color: darkMode ? "#B0B3B8" : "#666666",
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        {post.date && (
                          <div
                            className="flex items-center text-xs mb-2"
                            style={{ color: darkMode ? "#B0B3B8" : "#888888" }}
                          >
                            <FaCalendarAlt className="mr-1" />
                            {formatDate(post.date)}
                          </div>
                        )}

                        <h4
                          className="font-medium mb-1"
                          style={{ color: darkMode ? "#FFFFFF" : "#222222" }}
                        >
                          {post.title}
                        </h4>

                        <p
                          className="text-sm line-clamp-3 mb-2"
                          style={{ color: darkMode ? "#B0B3B8" : "#555555" }}
                        >
                          {post.body}
                        </p>

                        {post.document && (
                          <div className="mt-2 flex items-center">
                            <FaPaperclip
                              className="mr-1 text-sm"
                              style={{
                                color: darkMode ? "#B0B3B8" : "#888888",
                              }}
                            />
                            <span
                              className="text-xs"
                              style={{
                                color: darkMode ? "#B0B3B8" : "#888888",
                              }}
                            >
                              Document attached
                            </span>
                          </div>
                        )}

                        {post.poll_options && post.poll_options.length > 0 && (
                          <div className="mt-2 flex items-center">
                            <FaPoll
                              className="mr-1 text-sm"
                              style={{
                                color: darkMode ? "#B0B3B8" : "#888888",
                              }}
                            />
                            <span
                              className="text-xs"
                              style={{
                                color: darkMode ? "#B0B3B8" : "#888888",
                              }}
                            >
                              Poll: {post.poll_options.length} options
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Post footer */}
                      <div
                        className="flex items-center justify-between mt-3 pt-3 text-xs"
                        style={{
                          borderTop: darkMode
                            ? "1px solid #252525"
                            : "1px solid #E5E5E5",
                        }}
                      >
                        <span
                          style={{ color: darkMode ? "#B0B3B8" : "#888888" }}
                        >
                          Posted on {formatDate(post.created_at)}
                        </span>
                        <div className="flex space-x-4">
                          <button
                            className="flex items-center space-x-1 hover:text-[#A41E22] transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpvote(post.post_id);
                            }}
                            style={{
                              color: darkMode
                                ? post.has_upvoted
                                  ? "#A41E22"
                                  : "#B0B3B8"
                                : post.has_upvoted
                                  ? "#A41E22"
                                  : "#888888",
                            }}
                          >
                            {post.has_upvoted ? (
                              <FaThumbsUp className="text-red w-3 h-3" />
                            ) : (
                              <FaRegThumbsUp />
                            )}
                            <span>{post.upvotes}</span>
                          </button>
                          {activeTab === "My Posts" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePost(post.post_id);
                              }}
                              className="text-red-500 hover:text-red-700"
                              title="Delete post"
                            >
                              <FaTrash className="text-red" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!isLoading && posts.length > 0 && (
                <div className="flex justify-center mt-6">
                  <div className="flex space-x-2">
                    {currentPage > 1 && (
                      <button
                        onClick={() => fetchPosts(currentPage - 1, true)}
                        className="px-3 py-1 rounded-md"
                        style={{
                          backgroundColor: darkMode ? "#161B22" : "#E5E7EB",
                          color: darkMode ? "#F8F9FA" : "#1F252E",
                        }}
                      >
                        Previous
                      </button>
                    )}
                    {hasMore && (
                      <button
                        onClick={() => fetchPosts(currentPage + 1, true)}
                        className="px-3 py-1 rounded-md"
                        style={{
                          backgroundColor: darkMode ? "#161B22" : "#E5E7EB",
                          color: darkMode ? "#F8F9FA" : "#1F252E",
                        }}
                      >
                        Next
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Loading and empty states */}
              {isLoading && posts.length > 0 && (
                <div className="flex justify-center py-4">
                  <Loader />
                </div>
              )}
              {!hasMore && posts.length > 0 && (
                <div
                  className="text-center py-4 text-sm"
                  style={{ color: darkMode ? "#B0B3B8" : "#888888" }}
                >
                  No more posts to load
                </div>
              )}
              {!isLoading && posts.length === 0 && (
                <div
                  className="text-center py-10 text-sm"
                  style={{ color: darkMode ? "#B0B3B8" : "#888888" }}
                >
                  No posts found in this category
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Post Detail Modal */}
      <Modal isOpen={!!selectedPost} onClose={() => setSelectedPost(null)}>
        {selectedPost && (
          <div
            className="rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto flex flex-col"
            style={{
              backgroundColor: darkMode ? "#121212" : "#FFFFFF",
              color: darkMode ? "#FFFFFF" : "#222222",
              border: darkMode ? "1px solid #252525" : "1px solid #E5E5E5",
            }}
          >
            <div className="flex-1">
              {/* Modal header */}
              <div className="flex items-start space-x-4 mb-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: darkMode ? "#252525" : "#A41E22", // Red in light mode
                  }}
                >
                  <span
                    className="text-base font-medium"
                    style={{ color: "#FFFFFF" }}
                  >
                    {getInitials(selectedPost.user_name)}
                  </span>
                </div>
                <div>
                  <h3
                    className="font-medium text-lg"
                    style={{ color: darkMode ? "#FFFFFF" : "#A41E22" }}
                  >
                    {selectedPost.user_name}
                  </h3>
                  <p
                    className="text-sm"
                    style={{ color: darkMode ? "#B0B3B8" : "#888888" }}
                  >
                    {getUserRoleDisplay(selectedPost)}
                  </p>
                  {selectedPost.email_id && (
                    <a
                      href={`mailto:${selectedPost.email_id}`}
                      className="text-xs block mt-1 hover:underline"
                      style={{ color: darkMode ? "#A41E22" : "#A41E22" }}
                    >
                      {selectedPost.email_id}
                    </a>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedPost.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2 py-1 rounded-full"
                    style={{
                      backgroundColor: darkMode ? "#252525" : "#F0F0F0",
                      color: darkMode ? "#B0B3B8" : "#666666",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Event date */}
              {selectedPost.date && (
                <div
                  className="flex items-center text-sm mb-4"
                  style={{ color: darkMode ? "#B0B3B8" : "#888888" }}
                >
                  <FaCalendarAlt className="mr-2" />
                  {formatDate(selectedPost.date)}
                </div>
              )}

              {/* Post title */}
              <h2
                className="text-xl font-bold mb-3"
                style={{ color: darkMode ? "#FFFFFF" : "#222222" }}
              >
                {selectedPost.title}
              </h2>

              {/* Post content */}
              <div className="mb-6">
                <p
                  className="whitespace-pre-line"
                  style={{ color: darkMode ? "#B0B3B8" : "#555555" }}
                >
                  {selectedPost.body}
                </p>
              </div>

              {/* Document preview */}
              {selectedPost.document && (
                <div className="mb-6">
                  <h3
                    className="font-medium mb-2"
                    style={{ color: darkMode ? "#FFFFFF" : "#222222" }}
                  >
                    Attached Document
                  </h3>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/${selectedPost.document}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-[#A41E22] hover:underline"
                  >
                    <FaPaperclip className="mr-2" />
                    View Document
                  </a>
                </div>
              )}

              {/* Poll display */}
              {renderPollOptions(selectedPost)}
            </div>

            {/* Modal footer */}
            <div
              className="flex items-center justify-between mt-6 pt-4 border-t"
              style={{ borderColor: darkMode ? "#252525" : "#E5E5E5" }}
            >
              <span
                className="text-sm"
                style={{ color: darkMode ? "#B0B3B8" : "#888888" }}
              >
                Posted on {formatDate(selectedPost.created_at)}
              </span>
              <div className="flex space-x-4">
                <button
                  className="flex items-center space-x-1 hover:text-[#A41E22] transition-colors"
                  onClick={() => handleUpvote(selectedPost.post_id)}
                  style={{
                    color: darkMode
                      ? selectedPost.has_upvoted
                        ? "#A41E22"
                        : "#B0B3B8"
                      : selectedPost.has_upvoted
                        ? "#A41E22"
                        : "#888888",
                  }}
                >
                  {selectedPost.has_upvoted ? (
                    <FaThumbsUp className="text-[#A41E22]" />
                  ) : (
                    <FaRegThumbsUp />
                  )}
                  <span>{selectedPost.upvotes}</span>
                </button>
              </div>
              {activeTab === "My Posts" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePost(selectedPost.post_id);
                  }}
                  className="text-red-500 hover:text-red-700"
                  title="Delete post"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Toaster
        toastOptions={{
          duration: 3000,
          style: {
            background: darkMode ? "#121212" : "#FFFFFF",
            color: darkMode ? "#FFFFFF" : "#222222",
            border: darkMode ? "1px solid #252525" : "1px solid #E5E5E5",
            boxShadow: darkMode
              ? "0 4px 12px rgba(0, 0, 0, 0.5)"
              : "0 4px 12px rgba(0, 0, 0, 0.1)",
          },
        }}
      />
    </div>
  );
};

export default EchoesDTU;
